import React, { useState, useEffect, useCallback } from 'react';
import roleService from '../../services/roleService';
import { useToast } from '../../context/ToastContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MODULE_ORDER = ['DASHBOARD', 'ECMS', 'IFLEET', 'ZEEROW', 'TICKETS', 'USERS', 'ROLES'];

function groupByModule(actions) {
  const map = {};
  actions.forEach(a => {
    if (!map[a.module]) map[a.module] = [];
    map[a.module].push(a);
  });
  return map;
}

const BASE_ROLE_BADGE = {
  ADMIN:   'bg-purple-100 text-purple-700',
  KAM:     'bg-blue-100 text-blue-700',
  SUPPORT: 'bg-green-100 text-green-700',
};

// ── Role Create/Edit Modal ────────────────────────────────────────────────────

const RoleModal = ({ open, onClose, onSaved, allActions, editRole }) => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [name, setName]           = useState('');
  const [description, setDesc]    = useState('');
  const [selectedIds, setSelected] = useState(new Set());
  const [saving, setSaving]        = useState(false);
  const [expanded, setExpanded]    = useState({});

  useEffect(() => {
    if (editRole) {
      setName(editRole.name);
      setDesc(editRole.description || '');
      setSelected(new Set(editRole.actions.map(a => a.id)));
    } else {
      setName('');
      setDesc('');
      setSelected(new Set());
    }
    setExpanded({});
  }, [editRole, open]);

  const grouped = groupByModule(allActions);

  const toggleAction = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleModule = (mod) => {
    const modIds = (grouped[mod] || []).map(a => a.id);
    const allSelected = modIds.every(id => selectedIds.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      modIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  };

  const handleSave = async () => {
    if (!name.trim()) { toastError('Role name is required'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, actionIds: [...selectedIds] };
      if (editRole) {
        await roleService.updateRole(editRole.id, payload);
        toastSuccess('Role updated successfully');
      } else {
        await roleService.createRole(payload);
        toastSuccess('Role created successfully');
      }
      onSaved();
      onClose();
    } catch (e) {
      toastError(e?.response?.data?.error || e?.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-800">
            {editRole ? 'Edit Role' : 'Create Custom Role'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Role Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Billing Manager"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Description</label>
            <textarea
              value={description}
              onChange={e => setDesc(e.target.value)}
              rows={2}
              placeholder="Optional description for this role"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* Permissions matrix */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-600">Permissions</label>
              <span className="text-xs text-gray-400">{selectedIds.size} selected</span>
            </div>
            <div className="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
              {MODULE_ORDER.filter(m => grouped[m]).map(mod => {
                const modActions = grouped[mod] || [];
                const allSelected = modActions.every(a => selectedIds.has(a.id));
                const someSelected = modActions.some(a => selectedIds.has(a.id));
                const isExpanded = expanded[mod] !== false; // default expanded
                return (
                  <div key={mod}>
                    {/* Module header */}
                    <div
                      className="flex items-center justify-between px-4 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => setExpanded(prev => ({ ...prev, [mod]: !isExpanded }))}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => toggleModule(mod)}
                          onClick={e => e.stopPropagation()}
                          className="accent-primary w-3.5 h-3.5"
                        />
                        <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">{mod}</span>
                        <span className="text-xs text-gray-400">({modActions.filter(a => selectedIds.has(a.id)).length}/{modActions.length})</span>
                      </div>
                      <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    {/* Actions */}
                    {isExpanded && (
                      <div className="px-4 py-2 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {modActions.map(action => (
                          <label key={action.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 px-2 py-1 rounded-lg">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(action.id)}
                              onChange={() => toggleAction(action.id)}
                              className="accent-primary w-3.5 h-3.5 flex-shrink-0"
                            />
                            <span className="text-xs text-gray-700">{action.label}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-60 flex items-center gap-1.5"
          >
            {saving && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            {editRole ? 'Save Changes' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const RoleManagement = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [tab, setTab]               = useState('roles'); // 'roles' | 'assign'
  const [roles, setRoles]           = useState([]);
  const [allActions, setAllActions] = useState([]);
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editRole, setEditRole]     = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // role to delete
  const [assigning, setAssigning]   = useState({}); // userId → true

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, actionsRes, usersRes] = await Promise.all([
        roleService.getRoles(),
        roleService.getActions(),
        roleService.getUsers(),
      ]);
      setRoles(rolesRes.data || []);
      setAllActions(actionsRes.data || []);
      setUsers(usersRes.data || []);
    } catch {
      toastError('Failed to load role management data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleEdit = (role) => { setEditRole(role); setModalOpen(true); };
  const handleCreate = () => { setEditRole(null); setModalOpen(true); };

  const handleDelete = async (role) => {
    try {
      await roleService.deleteRole(role.id);
      toastSuccess(`Role "${role.name}" deleted`);
      fetchData();
    } catch (e) {
      toastError(e?.response?.data?.message || 'Failed to delete role');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleAssignRole = async (userId, customRoleId) => {
    setAssigning(prev => ({ ...prev, [userId]: true }));
    try {
      await roleService.assignRole({ userId, customRoleId: customRoleId || null });
      toastSuccess('Role assignment updated');
      const res = await roleService.getUsers();
      setUsers(res.data || []);
    } catch {
      toastError('Failed to update role assignment');
    } finally {
      setAssigning(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-8">

        {/* Page header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Role Management</h1>
            <p className="text-gray-500 text-sm mt-0.5">Create custom roles and assign permissions to users</p>
          </div>
          {tab === 'roles' && (
            <button
              onClick={handleCreate}
              className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-light transition-colors"
            >
              + Create Role
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 border-b border-gray-200">
          {[
            { key: 'roles',  label: `Custom Roles (${roles.length})` },
            { key: 'assign', label: `Assign to Users (${users.length})` },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* ── Tab 1: Custom Roles ─────────────────────────────────────── */}
            {tab === 'roles' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                {roles.length === 0 ? (
                  <div className="text-center py-16 text-gray-400">
                    <p className="text-4xl mb-3">🔐</p>
                    <p className="text-sm font-medium text-gray-500">No custom roles yet</p>
                    <p className="text-xs mt-1">Click "Create Role" to get started</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-100">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Permissions</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {roles.map(role => (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <span className="font-semibold text-gray-800">{role.name}</span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{role.description || '—'}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              {role.actions?.length ?? 0} actions
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400">
                            {role.createdAt ? new Date(role.createdAt).toLocaleDateString('en-IN') : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEdit(role)}
                                className="text-xs px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(role)}
                                className="text-xs px-3 py-1 border border-red-200 rounded-lg hover:bg-red-50 text-red-600"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {/* ── Tab 2: Assign Roles ──────────────────────────────────────── */}
            {tab === 'assign' && (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Base Role</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Custom Role</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Active</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {users.map(u => (
                      <tr key={u.userId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{u.name}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{u.email}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${BASE_ROLE_BADGE[u.role] || 'bg-gray-100 text-gray-600'}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <select
                              value={u.customRoleId || ''}
                              onChange={e => handleAssignRole(u.userId, e.target.value ? Number(e.target.value) : null)}
                              disabled={assigning[u.userId]}
                              className="border border-gray-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white disabled:opacity-60 min-w-[160px]"
                            >
                              <option value="">— None —</option>
                              {roles.map(r => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                            {assigning[u.userId] && (
                              <span className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {u.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <RoleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchData}
        allActions={allActions}
        editRole={editRole}
      />

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-bold text-gray-800 mb-2">Delete Role</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete <span className="font-semibold">"{deleteConfirm.name}"</span>?
              Users assigned to this role will lose their custom permissions on next login.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleManagement;
